import React, { useEffect, useState, useRef } from "react";

import gsap from "gsap";
import { useRouter } from "next/router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FaUniversity, FaUserFriends } from "react-icons/fa";
import { LuNewspaper } from "react-icons/lu";
import { searchGhostPosts } from "../../utils/ghostSearch";

const tagIconMap = {
  college: <FaUniversity className="mr-3 text-[#ff9245] text-lg" />,
  counselling: <FaUserFriends className="mr-3 text-[#ff9245] text-lg" />,
  blogs: <LuNewspaper className="mr-3 text-[#ff9245] text-lg" />,
};

export default function HeroFour() {
  const router = useRouter();

  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionType, setSessionType] = useState("UG");

  const debounceRef = useRef(null);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // We perform client-side Ghost API search with debounce

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleSearch = (e) => {
    const raw = (e && e.target && e.target.value) || "";
    const value = raw.toString();
    setSearchQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      const q = value.trim();
      if (!q || q.length < 2) {
        setSearchResults([]);
        setShowResults(!!q);
        return;
      }

      try {
        setIsLoadingResults(true);
        const postsResp = await searchGhostPosts(q);
        setSearchResults(postsResp);
        setShowResults(true);
      } catch (err) {
        console.error("Error searching posts via Ghost API:", err);
        setSearchResults([]);
        setShowResults(false);
      } finally {
        setIsLoadingResults(false);
      }
    }, 220);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleInputClick = () => {
    if (inputRef.current?.value) {
      setShowResults(true);
    }
  };

  const handleOpenSearch = () => {
    setIsSearchOpen(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleClickOutside = (event) => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setShowResults(false);
    }
  };


  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);


  // Proper body scroll lock when modal opens
  useEffect(() => {
    if (!isModalOpen) {
      const savedScrollY = parseInt(document.body.dataset.scrollY || "0", 10);

      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      document.body.style.touchAction = "";

      if (document.body.dataset.scrollY) {
        window.scrollTo(0, savedScrollY);
        delete document.body.dataset.scrollY;
      }

      return;
    }

    const scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.dataset.scrollY = String(scrollY);

    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.height = "100%";

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      const savedScrollY = parseInt(document.body.dataset.scrollY || "0", 10);

      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      document.body.style.touchAction = "";

      if (document.body.dataset.scrollY) {
        window.scrollTo(0, savedScrollY);
        delete document.body.dataset.scrollY;
      }
    };
  }, [isModalOpen]);

  // Parallax + sliding text
  useEffect(() => {
    if (typeof window === "undefined") return;

    let listeners = [];

    const parallaxIt = () => {
      const containers = document.querySelectorAll(".js-mouse-move-container");
      if (!containers.length) return [];

      const addedListeners = [];

      containers.forEach((container) => {
        const targets = container.querySelectorAll(".js-mouse-move");

        targets.forEach((el) => {
          const movement = el.getAttribute("data-move");

          const listener = (e) => {
            const relX = e.pageX - container.offsetLeft;
            const relY = e.pageY - container.offsetTop;

            gsap.to(el, {
              x:
                ((relX - container.offsetWidth / 2) / container.offsetWidth) *
                Number(movement),
              y:
                ((relY - container.offsetHeight / 2) / container.offsetHeight) *
                Number(movement),
              duration: 0.2,
            });
          };

          document.addEventListener("mousemove", listener);
          addedListeners.push(listener);
        });
      });

      return addedListeners;
    };

    const timeout = setTimeout(() => {
      const text1 = document.querySelector(".js-sliding-text-1");
      const text2 = document.querySelector(".js-sliding-text-2");

      if (text1 && text2) {
        const timeline = gsap.timeline({
          repeat: -1,
          defaults: { duration: 0.35 },
        });

        timeline
          .to(text1, {
            opacity: 1,
            ease: "power2.out",
            onStart: () => {
              text1.style.display = "block";
              text2.style.display = "none";
            },
          })
          .to(text1, { opacity: 0, delay: 1.1, ease: "power2.out" })
          .to(text2, {
            opacity: 1,
            ease: "power2.out",
            onStart: () => {
              text1.style.display = "none";
              text2.style.display = "block";
            },
          })
          .to(text2, { opacity: 0, delay: 1.1, ease: "power2.out" });
      }

      listeners = parallaxIt();
    }, 0);

    return () => {
      clearTimeout(timeout);
      listeners.forEach((listener) => {
        document.removeEventListener("mousemove", listener);
      });
    };
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <section className="masthead -type-3 bg-light-6 p-[1.75rem] js-mouse-move-container relative">
        <div className="container relative z-10">
          <div className="row items-center justify-center relative z-10">
            {/* Left Content */}
            <div
              className="col-xl-7 col-lg-11 relative z-10"
              data-aos="fade-up"
              data-aos-delay="500"
            >
              <div className="masthead__content">
                <h1 className="masthead__title sm:text-xl">
                  <span className="text-white">
                    Journey to Your Dream Medical College
                  </span>
                  <br className="hidden lg:block" />
                  <span className="text-orange-1 lg:inline block whitespace-nowrap">
                    Starts Right Here!
                  </span>
                </h1>

                <p className="masthead__text text-white mt-5 text-18">
                  NEET Support Driving Students to Success In NEET Counselling
                  Since 2022!
                  <br className="lg:d-none" />
                </p>

                {/* Search Bar */}
                <div
                  className="masthead-search mt-25 relative z-[9999]"
                  ref={searchRef}
                >
                  <div className="masthead-search__form">
                    <form onSubmit={handleSubmit} className="flex relative">
                      <input
                        type="text"
                        placeholder="Enter Keywords, Topics, Questions & more"
                        className="sm:placeholder:text-sm placeholder:text-[12px] sm:pl-6 pl-20 w-full border border-orange-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-300"
                        onChange={handleSearch}
                        onClick={handleInputClick}
                        ref={inputRef}
                        value={searchQuery}
                      />

                      <button
                        type="button"
                        className="button -orange-1 text-white ml-2"
                        onClick={handleOpenSearch}
                        aria-label="Open search"
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>

                      {isSearchOpen && (
                        <button
                          type="button"
                          className="button -outline-orange-1 text-orange-1 ml-2"
                          onClick={handleCloseSearch}
                          aria-label="Close search"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}

                      {showResults && searchResults.length > 0 && (
                        <ul className="absolute top-full left-0 w-full bg-white border border-gray-300 mt-1 max-h-60 overflow-y-auto rounded-lg shadow-lg z-[9999]">
                          {searchResults.map((post) => {
                            const matchingTag = post.tags?.find((tag) => {
                              const value = (tag?.name || tag?.slug || "").toLowerCase();
                              return Object.keys(tagIconMap).includes(value);
                            });

                            return (
                              <li
                                key={post.id}
                                className="flex items-center p-2 hover:bg-orange-100 cursor-pointer"
                                onClick={() => {
                                  const matchingTag = post.tags?.find((tag) => {
                                    const value = (tag?.name || tag?.slug || "").toLowerCase();
                                    return Object.keys(tagIconMap).includes(value);
                                  });
                                  const type = (matchingTag?.name || matchingTag?.slug || "").toLowerCase();
                                  const internalUrl =
                                    type === 'college'
                                      ? `/college/${post.slug}`
                                      : type === 'counselling'
                                      ? `/counselling/${post.slug}`
                                      : type === 'blogs'
                                      ? `/blogs/${post.slug}`
                                      : post.canonical_url || post.url;

                                  if (internalUrl) {
                                    router.push(internalUrl);
                                    setShowResults(false);
                                  } else {
                                    console.error("Post URL is missing");
                                  }
                                }}
                              >
                                {matchingTag &&
                                  tagIconMap[matchingTag.name.toLowerCase()]}
                                <span>{post.title}</span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </form>

                    {/* Book Session Button */}
                    <div className="flex justify-center mt-2 lg:mt-16">
                      <button
                        type="button"
                        onClick={handleOpenModal}
                        className="group relative px-6 py-3 sm:px-12 sm:py-4 
    rounded-full font-semibold text-sm sm:text-base text-[#ff9245] 
    bg-[#fff]
    shadow-[0_10px_30px_rgba(255,146,69,0.4)]
    transition-all duration-300 
    hover:scale-105 hover:shadow-[0_15px_40px_rgba(255,146,69,0.6)]
    active:scale-95 flex items-center gap-2"
                      >
                        {/* Glow Effect */}
                        <span className="absolute inset-0 rounded-full bg-gradient-to-r from-[#ff9245] to-[#ff7a1a] opacity-0 group-hover:opacity-30 blur-xl transition duration-300"></span>
                        {/* Button Content */}
                        <span className="relative flex items-center gap-2">
                          {/* Icon */}
                          <svg
                            className="w-4 h-4 sm:w-5 sm:h-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 7V3M16 7V3M3 11H21M5 5H19A2 2 0 0121 7V19A2 2 0 0119 21H5A2 2 0 013 19V7A2 2 0 015 5Z" />
                          </svg>
                          Book 1:1 Counselling Session
                          {/* Red Dot inside button */}
                          <span className="ml-2 relative flex items-center">
                            <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-ping absolute"></span>
                            <span className="w-2.5 h-2.5 bg-red-500 rounded-full relative"></span>
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="masthead-search__searches mt-40"></div>
                </div>
              </div>
            </div>

            {/* Right Content */}
            <div
              className="col-xl-5 col-lg-7 relative z-0"
              data-aos="fade-up"
              data-aos-delay="750"
            >
              <div className="hello masthead-image relative z-0">
                <div className="masthead-image__img1 relative z-0">
                  <div className="masthead-image__shape xl:d-none">
                    <img
                      src="./assets/images/shape.svg"
                      alt="Shape"
                      className="relative z-0"
                    />
                  </div>

                  <img
                    src="/assets/images/12.webp"
                    alt="Hero"
                    width="800"
                    height="600"
                    loading="lazy"
                    data-move="20"
                    className="js-mouse-move mt-[-160px] sm:mt-[-53px] ml-8 sm:ml-0 relative z-0"
                  />
                </div>

                <div className="masthead-image__el2 relative z-0">
                  <div className="shadow-4 img-el sm:px-10 bg-white sm:py-2 d-flex items-center rounded-lg js-mouse-move relative z-0">
                    <div className="img-el__side bg-orange-400 rounded-full">
                      <div className="size-50 d-flex justify-center items-center rounded-full">
                        <img
                          src="/assets/images/1.svg"
                          alt="Icon"
                          width="24"
                          height="24"
                          loading="lazy"
                        />
                      </div>
                    </div>

                    <div
                      className="position-relative px-6 pt-10 text-center text-15 fw-500 lh-1"
                      style={{
                        height: "40px",
                        width: "250px",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        className="position-absolute top-0 left-0 text-orange-400 js-sliding-text-1 text-[15px]"
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          opacity: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span className="text-xs sm:text-sm">
                          NEET Support Welcomes You!
                        </span>
                      </div>

                      <div
                        className="position-absolute top-0 left-0 text-orange-400 js-sliding-text-2 text-[15px]"
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          opacity: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span className="text-xs sm:text-sm">
                          Building Dreams, Building Future
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 1:1 Session Modal */}
      {isModalOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-50"
            style={{
              background: "rgba(20, 20, 20, 0.65)",
              backdropFilter: "blur(2px)",
            }}
            onClick={handleCloseModal}
          />
          {/* Modal */}
          <div
            className="fixed inset-0 z-50 flex justify-center items-start overflow-auto p-4"
            style={{ height: "100vh" }}
          >
            <div
              className="relative bg-white rounded-2xl shadow-2xl p-4 sm:p-6 flex flex-col items-center transition-all duration-300 pointer-events-auto"
              style={{
                width: "720px",
                maxWidth: "95vw",
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-[#ff9245] text-3xl font-bold rounded-full bg-white bg-opacity-80 shadow p-1 transition-all duration-200"
                onClick={handleCloseModal}
                aria-label="Close"
                style={{
                  lineHeight: "1",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                &times;
              </button>
              <h2 className="text-2xl font-bold mb-4 text-center text-[#005A53]">
                Book Your 1:1 Session with Our Mentors
              </h2>
              <div className="flex flex-wrap justify-center gap-4 mb-6">
                {["UG", "PG"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSessionType(type)}
                    aria-pressed={sessionType === type}
                    className={`px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${
                      sessionType === type
                        ? "bg-[#005A53] text-white shadow-lg"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Book {type} Session
                  </button>
                ))}
              </div>
              <iframe
                key={sessionType}
                src={
                  sessionType === "UG"
                    ? "https://admin.neetsupport.com/widget/bookings/neetsupport-one-to-one-ug-website?hideHeader=true&v=booking-header-2"
                    : "https://admin.neetsupport.com/widget/bookings/neetsupport-one-to-one-pg-website?hideHeader=true&v=booking-header-2"
                }
                width="100%"
                height="720"
                frameBorder="0"
                style={{ border: 0, minWidth: "320px" }}
                title={`1:1 session ${sessionType} Website Calendar`}
              ></iframe>
            </div>
          </div>
        </>
      )}
    </>
  );
}